'use client';

import { useTranslations } from 'next-intl';
import {
  ManagerIcon,
  SpecialistsIcon,
  ControlIcon,
  AwardIcon,
  ToolsIcon,
} from '@/components/ui/icons/benefitIcons';
import FadeInSection from '@/components/ui/FadeInSection';
import { StageMarker } from '@/components/layout/StoryRail';
import { SmartImage } from '@/components/ui/Skeleton';
import { aboutImage } from '@/lib/media';

export default function AboutSection() {
  const t = useTranslations('about');

  // benefit4 holds two points separated by a blank line — render each as its own
  // row so both get an icon. Icons reuse the shared benefit icon set.
  const benefit4Parts = t('benefit4').split('\n\n').map((s) => s.trim()).filter(Boolean);
  const benefits = [
    { Icon: ManagerIcon, text: t('benefit1') },
    { Icon: SpecialistsIcon, text: t('benefit2') },
    { Icon: ControlIcon, text: t('benefit3') },
    { Icon: AwardIcon, text: benefit4Parts[0] },
    { Icon: ToolsIcon, text: benefit4Parts[1] },
  ].filter((b) => b.text);

  const contacts = [
    {
      name: t('phone1Name'),
      number: t('phone1Number'),
      channels: t('phone1Channels'),
    },
    {
      name: t('phone2Name'),
      number: t('phone2Number'),
      channels: t('phone2Channels'),
    },
  ];

  return (
    <section id="o-nas" className="relative scroll-mt-2 border-t border-surface-bright/15 bg-surface-bright/5 px-margin-mobile py-section-gap lg:scroll-mt-4 lg:px-margin-desktop">
      <StageMarker n={2} />
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-start gap-stack-lg lg:grid-cols-12 lg:gap-16">
        {/* Photo */}
        <FadeInSection direction="right" className="order-2 lg:order-1 lg:col-span-6">
          <div className="forest-img-shadow relative aspect-[4/5] overflow-hidden rounded-sm border border-surface-bright/20">
            <SmartImage
              src={aboutImage}
              alt={t('imageAlt')}
              fill
              sizes="(max-width: 1024px) 100vw, 48vw"
              className="ken-burns object-cover"
            />
          </div>
        </FadeInSection>

        {/* Text + contacts */}
        <div className="order-1 lg:order-2 lg:col-span-6">
          {/* Heading slides in from the left, the rest from the right. */}
          <FadeInSection direction="right" distance={64}>
            <p className="text-label-caps uppercase tracking-widest text-primary">{t('eyebrow')}</p>
            <h2 className="mt-stack-md font-headline text-headline-lg font-bold tracking-[0.02em] text-on-surface">
              {t('headline')}
            </h2>
          </FadeInSection>

          <FadeInSection direction="left" distance={64} delay={0.15}>
            <ul className="mt-stack-lg space-y-stack-md">
              {benefits.map(({ Icon, text }) => (
                <li key={text} className="flex gap-stack-md">
                  <Icon className="mt-0.5 shrink-0 text-primary" />
                  <span className="text-body-md text-on-surface md:text-body-lg">{text}</span>
                </li>
              ))}
            </ul>

            <p className="mt-stack-lg text-label-caps uppercase tracking-widest text-outline">
              {t('phones')}
            </p>
            <div className="mt-stack-md grid grid-cols-1 gap-6 sm:grid-cols-2">
              {contacts.map((c) => (
                <a
                  key={c.number}
                  href={`tel:${c.number.replace(/\s+/g, '')}`}
                  className="block forest-card p-5 transition-colors hover:border-primary/50"
                >
                  <p className="text-label-caps uppercase tracking-widest text-primary">{c.name}</p>
                  <p className="mt-2 font-headline text-headline-md font-bold text-primary">{c.number}</p>
                  <p className="mt-2 text-label-caps uppercase tracking-widest text-outline">{c.channels}</p>
                </a>
              ))}
            </div>
          </FadeInSection>
        </div>
      </div>
    </section>
  );
}
